import { useEffect, useState } from 'react';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useClientPaginatedRows } from '@/hooks/usePagination';
import type { CrmReportsBundle } from '@/types/crm';
import { showApiError } from '@/utils/apiError';

export function CrmReportsPage() {
  const [reports, setReports] = useState<CrmReportsBundle | null>(null);
  const leadSourceReport = reports?.leadSourceReport ?? [];
  const salespersonReport = reports?.salespersonReport ?? [];
  const { rows: paginatedLeadSource, tablePaginationProps: leadSourcePaginationProps } =
    useClientPaginatedRows(leadSourceReport);
  const { rows: paginatedSalesperson, tablePaginationProps: salespersonPaginationProps } =
    useClientPaginatedRows(salespersonReport);

  useEffect(() => {
    crmApi.reports().then(setReports).catch((err) => showApiError(err, 'Reports unavailable'));
  }, []);

  const forecast = reports?.pipelineReport;

  return (
    <div>
      <PageHeader
        title="CRM Reports"
        description="Pipeline, conversion, lead source, and salesperson performance"
        breadcrumbs={[{ label: 'CRM', to: '/crm' }, { label: 'Reports' }]}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        <div className="panel">
          <p className="text-sm text-slate-500">Pipeline value</p>
          <p className="text-2xl font-semibold">{forecast?.pipelineValue ?? '—'}</p>
        </div>
        <div className="panel">
          <p className="text-sm text-slate-500">Weighted forecast</p>
          <p className="text-2xl font-semibold">{forecast?.weightedRevenue ?? '—'}</p>
        </div>
        <div className="panel">
          <p className="text-sm text-slate-500">Won revenue</p>
          <p className="text-2xl font-semibold">{forecast?.wonRevenue ?? '—'}</p>
        </div>
        <div className="panel">
          <p className="text-sm text-slate-500">Lead conversion</p>
          <p className="text-2xl font-semibold">
            {reports?.conversionReport?.conversionRate != null
              ? `${reports.conversionReport.conversionRate}%`
              : '—'}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel">
          <h3 className="font-semibold mb-3">Lead source report</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="pb-2">Source</th>
                <th>Leads</th>
                <th>Converted</th>
                <th>Rate</th>
              </tr>
            </thead>
            <tbody>
              {paginatedLeadSource.map((row) => (
                <tr key={row.leadSource} className="border-b border-slate-100">
                  <td className="py-2">{row.leadSource}</td>
                  <td>{row.leadCount}</td>
                  <td>{row.convertedCount}</td>
                  <td>{row.conversionRate}%</td>
                </tr>
              ))}
              {!leadSourceReport.length && (
                <tr>
                  <td colSpan={4} className="py-4 text-slate-500">
                    No lead source data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <TablePagination {...leadSourcePaginationProps} hasRows={leadSourceReport.length > 0} />
        </div>

        <div className="panel">
          <h3 className="font-semibold mb-3">Salesperson report</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="pb-2">Rep ID</th>
                <th>Open</th>
                <th>Won</th>
                <th>Revenue</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSalesperson.map((row) => (
                <tr key={row.employeeId} className="border-b border-slate-100">
                  <td className="py-2">{row.employeeId}</td>
                  <td>{row.openDeals}</td>
                  <td>{row.wonDeals}</td>
                  <td>{row.wonRevenue}</td>
                </tr>
              ))}
              {!salespersonReport.length && (
                <tr>
                  <td colSpan={4} className="py-4 text-slate-500">
                    No salesperson data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <TablePagination {...salespersonPaginationProps} hasRows={salespersonReport.length > 0} />
        </div>
      </div>
    </div>
  );
}
